// Share utility functions for product sharing and order placement

export interface ShareData {
  productName: string;
  price: number;
  productUrl?: string;
}

export interface OrderData {
  productId: string;
  productName: string;
  price: number;
  originalPrice?: number;
  quantity?: number;
  productUrl?: string;
  image?: string;
}

export interface LocalOrder {
  id: string;
  productId: string;
  productName: string;
  price: number;
  quantity: number;
  totalAmount: number;
  createdAt: string;
  status: "pending";
}

// Generate order ID
const generateOrderId = (): string => {
  return `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Format order message for WhatsApp
export const formatOrderMessage = (order: OrderData): string => {
  const quantity = order.quantity || 1;
  const totalAmount = order.price * quantity;

  let message = `🛒 *NEW ORDER REQUEST*\n\n`;
  message += `*Product:* ${order.productName}\n`;
  message += `*Quantity:* ${quantity}\n`;
  message += `*Price per unit:* ₹${order.price.toLocaleString("en-IN")}\n`;

  if (order.originalPrice && order.originalPrice > order.price) {
    const discount = order.originalPrice - order.price;
    message += `*Original Price:* ₹${order.originalPrice.toLocaleString(
      "en-IN"
    )}\n`;
    message += `*Discount:* ₹${discount.toLocaleString("en-IN")}\n`;
  }

  message += `*Total Amount:* ₹${totalAmount.toLocaleString("en-IN")}\n\n`;

  if (order.productUrl) {
    message += `*Product Link:* ${order.productUrl}\n\n`;
  }

  message += `Please confirm this order and provide payment details. Thank you! 🙏`;

  return message;
};

// Store order locally
export const storeOrderLocally = (
  order: OrderData,
  orderId: string
): LocalOrder => {
  const quantity = order.quantity || 1;

  const localOrder: LocalOrder = {
    id: orderId,
    productId: order.productId,
    productName: order.productName,
    price: order.price,
    quantity: quantity,
    totalAmount: order.price * quantity,
    createdAt: new Date().toISOString(),
    status: "pending",
  };

  // Get existing orders from localStorage
  const existingOrders = getLocalOrders();
  existingOrders.push(localOrder);

  // Store updated orders
  localStorage.setItem("benipal_orders", JSON.stringify(existingOrders));

  return localOrder;
};

// Get all local orders
export const getLocalOrders = (): LocalOrder[] => {
  try {
    const ordersJson = localStorage.getItem("benipal_orders");
    if (!ordersJson) return [];
    return JSON.parse(ordersJson);
  } catch (error) {
    console.error("Error reading orders from localStorage:", error);
    return [];
  }
};

// Place order and redirect to WhatsApp
export const placeOrder = async (
  order: OrderData,
  phoneNumber: string
): Promise<boolean> => {
  try {
    // Generate order ID once
    const orderId = generateOrderId();

    // Store order locally
    storeOrderLocally(order, orderId);

    // Format WhatsApp message with the same order ID
    const message = formatOrderMessage(order);

    // Create WhatsApp URL
    // Remove any non-digit characters from phone number for WhatsApp URL
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(
      message
    )}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, "_blank");

    return true;
  } catch (error) {
    console.error("Error placing order:", error);
    return false;
  }
};

// Legacy share function (kept for backward compatibility)
export const shareProduct = async (data: ShareData): Promise<boolean> => {
  const message = `I'm interested in ${
    data.productName
  } from Benipal Toys & Gift Store. Price: ₹${data.price.toLocaleString(
    "en-IN"
  )}. Contact: +91 87259 04949`;

  // Try Web Share API first (mobile devices)
  if (navigator.share) {
    try {
      await navigator.share({
        title: `${data.productName} - Benipal Toys`,
        text: message,
        url: data.productUrl || window.location.origin,
      });
      return true;
    } catch (error) {
      console.log("Web Share API failed:", error);
    }
  }

  // Fallback to WhatsApp
  const whatsappUrl = `https://wa.me/918725904949?text=${encodeURIComponent(
    message
  )}`;
  window.open(whatsappUrl, "_blank");
  return true;
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.log("Clipboard API failed:", error);
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      return true;
    } catch (fallbackError) {
      console.log("Fallback copy failed:", fallbackError);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};
